import React from 'react';

/**
 * /tmp labs Logo Component - Standardized Brand Asset
 *
 * Size Variants:
 * | Size Prop | Tailwind Class | Height (px) | Usage                           |
 * |-----------|----------------|-------------|---------------------------------|
 * | mobile    | h-10 w-auto    | 40px        | Mobile headers, compact layouts |
 * | tablet    | h-12 w-auto    | 48px        | Tablet-sized screens            |
 * | desktop   | h-12 w-auto    | 48px        | Desktop headers, navigation     |
 * | landing   | h-14 w-auto    | 56px        | Landing page hero sections      |
 * | large     | h-14 w-auto    | 56px        | Large display areas             |
 *
 * SVG Details:
 * | Property     | Value          |
 * |--------------|----------------|
 * | ViewBox      | 0 0 220 50     |
 * | Min Width    | 120px          |
 * | Aspect Ratio | 220:50 (4.4:1) |
 *
 * Colors:
 * | Element         | Color              |
 * |-----------------|--------------------|
 * | /tmp text       | #10B981 (emerald)  |
 * | Labs text       | #10B981 (emerald)  |
 * | Infinity symbol | #0D9488 (teal)     |
 * | Pulsing circle  | #0D9488 (teal)     |
 * | Cursor          | #10B981 (emerald)  |
 *
 * Usage Examples:
 * <TmpLabsLogo size="landing" showCursor={true} />
 * <TmpLabsLogo size="desktop" showCursor={true} />
 * <TmpLabsLogo size="mobile" showCursor={false} />
 */

const SIZE_CLASSES = {
  mobile: 'h-10 w-auto',   // 40px
  tablet: 'h-12 w-auto',   // 48px
  desktop: 'h-12 w-auto',  // 48px
  landing: 'h-14 w-auto',  // 56px
  large: 'h-14 w-auto',    // 56px
};

const TmpLabsLogo = ({
  size = 'desktop',
  showCursor = true,
  className = '',
  animate = true
}) => {
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.desktop;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 220 50"
      className={`${sizeClass} ${className}`}
      style={{ minWidth: '120px' }}
      role="img"
      aria-label="/tmp labs logo"
    >
      <style>
        {`
          .tmplabs-label {
            font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', 'Droid Sans Mono', 'Source Code Pro', monospace;
            font-size: 22px;
            fill: #10B981;
            font-weight: 500;
          }
          .tmplabs-labs {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 18px;
            fill: #10B981;
            font-weight: 600;
          }
          ${showCursor && animate ? `
          .tmplabs-cursor {
            animation: tmplabs-blink 1s step-end infinite;
          }
          @keyframes tmplabs-blink {
            from, to { opacity: 0; }
            50% { opacity: 1; }
          }
          ` : ''}
          ${animate ? `
          .tmplabs-pulse {
            fill: #0D9488;
            animation: tmplabs-pulseAnim 2s ease-in-out infinite;
          }
          @keyframes tmplabs-pulseAnim {
            0% { r: 5; opacity: 0.6; }
            50% { r: 8; opacity: 1; }
            100% { r: 5; opacity: 0.6; }
          }
          ` : `
          .tmplabs-pulse {
            fill: #0D9488;
          }
          `}
        `}
      </style>

      {/* Infinity Symbol */}
      <path
        d="M20,15 Q30,5 40,15 Q50,25 40,35 Q30,45 20,35 Q10,25 20,15 Z"
        fill="none"
        stroke="#0D9488"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      {/* Pulsing Circle */}
      <circle cx="30" cy="25" r="5" className="tmplabs-pulse" />

      {/* /tmp text */}
      <text x="60" y="32" className="tmplabs-label">/tmp</text>

      {/* Labs text */}
      <text x="118" y="32" className="tmplabs-labs">Labs</text>

      {/* Blinking Cursor */}
      {showCursor && (
        <text x="168" y="32" className={`tmplabs-label ${animate ? 'tmplabs-cursor' : ''}`}>|</text>
      )}
    </svg>
  );
};

// Named export for tree-shaking
export { TmpLabsLogo };

// Default export for convenience
export default TmpLabsLogo;
